import React, { useState } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from 'styles/ThemeTyping';
import classNames from 'classnames';
import * as Types from 'pages/RolesAndPermissions/typings';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Popper from '@material-ui/core/Popper';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import Filter from './Filter';
import SelectInput from './valueInput/SelectInput';

interface Props {
  filterGroup: Types.FilterGroup;
  filterGroups: Array<Types.FilterGroup>;
  dropdownContentType: Types.DropdownContentType;
  filterIdInFocus?: string;
  disabled: boolean;
  onDeleteFilter: (groupId: string, filterId: string) => void;
  onAddFilter: (groupId: string) => void;
  onOpenDropdown: (e: React.MouseEvent<HTMLElement>) => void;
  onSetDropdownAnchorEl: (el: HTMLElement | null) => void;
  onSetFilterIdInFocus: (filterId: string) => void;
  onSetFilterGroups: (newFilterGroups: Array<Types.FilterGroup>) => void;
  onSetDropdownContentType: (type: Types.DropdownContentType) => void;
  onUpdateFilteredUsers?: (
    groups?: Array<Types.FilterGroup>,
    outerOperator?: Types.Operator,
  ) => void;
}

function FilterGroup({
  filterGroup,
  filterGroups,
  dropdownContentType,
  filterIdInFocus,
  disabled,
  onDeleteFilter,
  onAddFilter,
  onOpenDropdown,
  onSetDropdownAnchorEl,
  onSetFilterIdInFocus,
  onSetDropdownContentType,
  onSetFilterGroups,
  onUpdateFilteredUsers,
}: Props) {
  const classes = useStyles();
  const { id, filters, operator } = filterGroup;
  const [innerOperatorAnchorEl, setInnerOperatorAnchorEl] = useState<null | HTMLElement>(null);

  const handleChangeInnerOperator = (groupId: string, newOperator: Types.Operator) => {
    setInnerOperatorAnchorEl(null);
    const newFilterGroups = filterGroups.map(group =>
      group.id === groupId ? { ...group, operator: newOperator as Types.Operator } : group,
    );

    onSetFilterGroups(newFilterGroups);
    onUpdateFilteredUsers && onUpdateFilteredUsers(newFilterGroups);
  };

  return (
    <Box display="flex" flexWrap="wrap" className={classes.root}>
      {filters.map((filter, index) => (
        <React.Fragment key={`filter-${index}`}>
          <Filter
            filter={filter}
            isFirstInGroup={index === 0}
            onDelete={() => onDeleteFilter(id, filter.id)}
            onOpenDropdown={onOpenDropdown}
            onSetDropdownAnchorEl={onSetDropdownAnchorEl}
            filterIdInFocus={filterIdInFocus}
            onSetFilterIdInFocus={onSetFilterIdInFocus}
            dropdownContentType={dropdownContentType}
            onSetDropdownContentType={onSetDropdownContentType}
            disabled={disabled}
          />
          {index < filters.length - 1 && (
            <>
              <Button
                variant="outlined"
                className={classNames(classes.outlinedButton, classes.operatorButton)}
                onClick={e => setInnerOperatorAnchorEl(e.currentTarget)}
                disabled={disabled}
              >
                {operator}
              </Button>
              <Popper
                open={!!innerOperatorAnchorEl}
                anchorEl={innerOperatorAnchorEl}
                transition
                disablePortal
                placement="bottom"
                className={classes.outerOperatorPopper}
              >
                <Paper>
                  <SelectInput
                    options={['and', 'or']}
                    onSelect={(selected: 'and' | 'or') => {
                      handleChangeInnerOperator(id, selected as Types.Operator);
                    }}
                    className={classes.operatorSelect}
                  />
                </Paper>
              </Popper>
            </>
          )}
        </React.Fragment>
      ))}
      {filters.length ? (
        <Tooltip title={disabled ? '' : 'Add Filter to Group'} placement="top" arrow>
          <Box display="flex">
            <Button
              onClick={() => onAddFilter(id)}
              className={classNames(classes.outlinedButton, classes.plusButton)}
              variant="outlined"
              disabled={disabled}
            >
              +
            </Button>
          </Box>
        </Tooltip>
      ) : null}
    </Box>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    rowGap: `${theme.spacing()}px`,
  },
  outlinedButton: {
    borderColor: 'rgba(36, 23, 23, 0.1)',
    borderRadius: 0,
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    minWidth: 0,
  },
  plusButton: {
    borderLeft: 'none',
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  },
  operatorButton: {
    fontWeight: theme.typography.fontWeightSemiBold,
  },
  outerOperatorPopper: {
    zIndex: theme.zIndex.modal,
  },
  operatorSelect: {
    textTransform: 'capitalize',
  },
}));

export default FilterGroup;
